<template>
    <div class="sticky top-0">
        <div class="campaign-filters__container border rounded-4 p-16 space-y-8">
            <SimpleInput v-model="filters.search" :placeholder="text.search">
                <template #after>
                    <SvgIcon type="material-search" class="self-center w-24 h-24 mr-8"></SvgIcon>
                </template>
            </SimpleInput>
            <div class="font-semibold">
                <p class="first-letter:uppercase font-semibold text-black mb-8">{{ text.channel }}</p>
                <div class="flex flex-wrap gap-8">
                    <div
                        class="flex border rounded-4 cursor-pointer p-8 grow"
                        @click="setTab('contracts')"
                    >
                        <RadioButton :value="tab" checked="contracts" class="mr-8" />
                        <span>{{ `${$t('multi-posting.tabs.contracts.title')} (${contractsCount})` }}</span>
                    </div>
                    <div
                        class="flex border rounded-4 cursor-pointer p-8 grow"
                        @click="setTab('socialChannels')"
                    >
                        <RadioButton :value="tab" checked="socialChannels" class="mr-8" />
                        <span>{{ $t('multi-posting.channels.social') }}</span>
                    </div>
                    <div
                        class="flex border rounded-4 cursor-pointer p-8 grow"
                        @click="setTab('allProducts')"
                    >
                        <RadioButton :value="tab" checked="allProducts" class="mr-8" />
                        <span>{{ $t('multi-posting.channels.all') }}</span>
                    </div>
                </div>
            </div>
            <div v-if="tab === 'allProducts'" class="space-y-8">
                <div>
                    <p class="first-letter:uppercase font-semibold text-black mb-8">{{ text.sortBy }}</p>
                    <SelectDropdown v-model="filters.sortBy" :options="sortOptions" :auto-translate="false" />
                </div>
                <div class="flex justify-between cursor-pointer" @click="collapseFilters = !collapseFilters">
                    <p class="text-body font-semibold mb-8">{{ $t('general.filter') }}</p>
                    <SvgIcon
                        type="keyboard-arrow-down"
                        :class="{'rotate-180': collapseFilters }"
                        size="24"
                        class="transition-transform"
                    />
                </div>
                <div v-if="collapseFilters">
                    <banner
                        inline
                        :description="$t('multi-posting.campaigns.filters.description').toString()"
                        class="!mt-0"
                    />
                    <div class="my-8">
                        <p class="first-letter:uppercase font-semibold text-black mb-8">{{ $t('multi-posting.campaigns.filters.job_title') }}</p>
                        <SelectDropdown v-model="filters.jobTitleId" :options="lookups.jobTitles" :auto-translate="false"
                            searchable remove-icon keep-search-after-selection :truncate="true" :loading="fetchingJobTitles"
                            @search="search($event, 'jobtitles')" />
                    </div>
                    <div class="my-8">
                        <p class="first-letter:uppercase font-semibold text-black mb-8">{{ $t('multi-posting.campaigns.filters.job_function') }}</p>
                        <SelectDropdown v-model="filters.jobFunctionId" :options="jobFunctionOptions"
                            :auto-translate="false" searchable keep-search-after-selection :truncate="true" :remove-icon="!filters.jobTitleId" />
                    </div>
                    <div class="my-8">
                        <p class="first-letter:uppercase font-semibold text-black mb-8">{{ $t('multi-posting.campaigns.filters.industries') }}</p>
                        <SelectDropdown v-model="filters.industryId" :options="lookups.industries" :auto-translate="false"
                            searchable remove-icon keep-search-after-selection :truncate="true" />
                    </div>
                    <div class="my-8">
                        <p class="first-letter:uppercase font-semibold text-black mb-8">{{ $t('multi-posting.campaigns.filters.location') }}</p>
                        <SelectDropdown v-model="filters.locationId" :options="locations" :auto-translate="false" searchable
                            remove-icon keep-search-after-selection :truncate="true" @search="search($event, 'locations')"
                            :loading="fetchingLocations" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SimpleInput from "../../forms/Input.vue";
import SvgIcon from "../../ui/SvgIcon.vue";
import _ from "lodash";
import RadioButton from "../../forms/RadioButton.vue";
import SelectDropdown from "../../forms/SelectDropdown.vue";
import {mapState, mapActions} from "pinia";
import {useMainStore} from '../../../state/store';
import {useMultiPostingStore} from '../../../state/modules/multiPosting';
import * as Vonq from "/../../vendor/jobtoolz/vonq/public/js/share/Vonq.js"
import Banner from "../../ui/Banner.vue";
import _debounce from "lodash/debounce";

export default {
    name: 'ProductFilters',
    components: {Banner, SelectDropdown, RadioButton, SimpleInput, SvgIcon },
    data() {
        return {
            collapseFilters: true,
            fetchingJobTitles: false,
            fetchingLocations: false,
            lastRelevantState: false,
            debouncedFilterAction: null,
            debouncedSearch: {
                jobtitles: null,
                locations: null,
            }
        }
    },
    props: {
        contractsCount: { type: Number, default: 0, required: true }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        ...mapState(useMultiPostingStore, ['lookups', 'filters', 'pagination', 'tab']),
        text() {
            return {
                search: `${_.capitalize(this.$t('general.search').toString())}...`,
                channel: _.capitalize(this.$t('campaigns.channels').toString()),
                sortBy: _.capitalize(this.$t('general.sort_by').toString())
            }
        },
        sortOptions() {
            return [
                { value: 'relevant', label: this.$t('general.recommended_first') },
                { value: 'recent', label: this.$t('general.filters.recent') },
                { value: 'order_frequency.asc', label: this.$t('general.filters.order_frequency.asc') },
                { value: 'order_frequency.desc', label: this.$t('general.filters.order_frequency.desc') },
                { value: 'created.asc', label: this.$t('general.filters.created_asc') },
                { value: 'created.desc', label: this.$t('general.filters.created_desc') },
                { value: 'list_price.asc', label: this.$t('general.filters.list_price_asc') },
                { value: 'list_price.desc', label: this.$t('general.filters.list_price_desc') },
            ];
        },
        locations(vm) {
            return vm.lookups.locations;
        },
        jobFunctionOptions() {
            return this.filters.jobTitleId ?
                this.lookups.jobFunctions.filter(x => x.value === this.filters.jobFunctionId) :
                this.lookups.jobFunctions;
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, [
            'updateForm',
            'resetPagination',
            'setTab',
            'fetchTaxonomyJobFunctions',
            'fetchTaxonomyIndustries',
            'fetchProductTypes',
            'fetchTaxonomyJobTitles',
            'fetchTaxonomyLocations',
            'fetchProducts',
            'filterBoosters',
            'filterContracts'
        ]),
        search(event, type) {
            if (this.debouncedSearch[type]) {
                this.debouncedSearch[type](event);
            }
        },
        handleFilterAction() {
            if (this.tab === "socialChannels") {
                this.filterBoosters(this.locale);
            } else if (this.tab === "contracts") {
                this.filterContracts()
            } else {
                this.fetchProducts({ pagination: this.pagination});
            }
        }
    },
    watch: {
        filters: {
            handler() {
                this.resetPagination();

                if (this.filters.jobTitleId && !this.filters.jobFunctionId) {
                    const title = this.lookups.jobTitles.find(x => x.value === this.filters.jobTitleId);
                    this.filters.jobFunctionId = title ? title.jobFunction.id : null;
                }

                const hasRelevantFilters = !!(
                    this.filters.jobTitleId ||
                    this.filters.jobFunctionId ||
                    this.filters.industryId ||
                    this.filters.locationId
                );

                if (hasRelevantFilters && !this.lastRelevantState) {
                    if (this.filters.sortBy !== 'relevant') {
                        this.filters.sortBy = 'relevant';
                    }
                } else if (!hasRelevantFilters && this.lastRelevantState) {
                    if (this.filters.sortBy === 'relevant') {
                        this.filters.sortBy = 'list_price.asc';
                    }
                }

                this.lastRelevantState = hasRelevantFilters;

                this.debouncedFilterAction();
            },
            deep: true
        }
    },
    created() {
        Vonq.synchronousPromises([
            this.fetchTaxonomyJobFunctions(),
            this.fetchTaxonomyIndustries(),
            this.fetchProductTypes(),
        ]);

        this.debouncedFilterAction = _debounce(this.handleFilterAction, 250);

        this.debouncedSearch.jobtitles = _.debounce((event) => {
            this.fetchingJobTitles = true;
            this.fetchTaxonomyJobTitles(event)
                .finally(() => this.fetchingJobTitles = false);
        }, 250);

        this.debouncedSearch.locations = _.debounce((event) => {
            this.fetchingLocations = true;
            this.fetchTaxonomyLocations(event)
                .finally(() => this.fetchingLocations = false);
        }, 250);
    },
    beforeUnmount() {
        this.resetPagination();
    }
}
</script>

<style>
.campaign-filters__container {
    max-height: 85vh;
    overflow-y: auto;
}
</style>
